// Summoned creatures («Заклинатели»): short-lived units a hero calls up — Птицелов's magpies, Кукольник's golem,
// Волчица's wolves. Same shape as monsters.ts: a data table plus functions over (Game, …), so game.ts only wires
// hooks in.
//
// Rules, decided once here:
// - A summon is on its owner's team and everything it deals is the owner's: damage() gets src = owner (stats,
//   kills, assists), with the owner's own damage buffs left out (skipSrcBuff) and the hit coming from the summon's
//   spot (fromPos — Стойка faces the creature, not its master).
// - Enemies hit it with whatever hits a monster: basic attacks, every hostilesOf() skill, damaging zones and
//   Прорицание. Like a monster it only ever loses hp — no stun, slow, knockback, heal or shield.
// - Seen like a totem: through the ordinary fog, and in tall grass only through a reveal or up close. A pet (a
//   magpie or a wolf, not the autonomous golem) also hides with its owner: within PET_HIDE of an owner the team
//   can't see (grass, Слиться, fog), it can't be seen either — until it strays further off.
// - It outlives its owner: the owner's death dismisses nothing, only the lifetime or its hp end it.
// - AI, re-decided every RETARGET s. Magpies and wolves take the owner's own attack target first, then the
//   nearest enemy near them — heroes and other summons, a monster only when the owner is attacking it — and
//   chase nothing further than `leash` from a living owner; with no one to fight they walk after the owner.
//   The golem answers to no one: the nearest enemy hero, enemy summon or monster within `aggro` of itself, kept
//   while it stays that close; with none, it walks to the nearest capture point its team doesn't hold (the nearest
//   point at all when it holds them all) and stands on it.
import * as THREE from 'three';
import { LAYOUT, type Team } from './layout.js';
import type { Point } from './nav.js';
import { maxHpOf, type DamageMark } from './artifacts.js';
import { PASSIVES } from './passives.js';
import { SKILLS } from './skills.js';
import { isHero, isMonster, bulkOf, type Target } from './monsters.js';
import type { Game, Hero, HeroState } from './game.js';

export type SummonKind = 'magpie' | 'golem' | 'wolf';
export type SummonId = number;

// `fly`: straight over walls and trees, no path.
export const SUMMONS = {
  magpie: { hp: 20, speed: 8, dmg: 4, attackCd: 1, range: 1.2, radius: 0.3, aggro: 7, leash: 12, fly: true },
  golem: { hp: 100, speed: 3.4, dmg: 14, attackCd: 1.6, range: 1.6, radius: 0.7, aggro: 8, leash: 8, fly: false },
  wolf: { hp: 60, speed: 6, dmg: 6, attackCd: 1.1, range: 1.5, radius: 0.45, aggro: 8, leash: 14, fly: false },
} as const satisfies Record<SummonKind, { hp: number; speed: number; dmg: number; attackCd: number; range: number; radius: number; aggro: number; leash: number; fly: boolean }>;

const RETARGET = 0.25;
/** m: a pet this close to an owner the enemy can't see hides with him. */
export const PET_HIDE = 4;
const REPATH = 0.3;
/* A magpie's peck holds back the windup a hero is in the middle of (Стая сорок). */
const PECK_DELAYS = new Set<HeroState>(['casting', 'windup', 'lungeWindup']);

export interface Summon {
  id: SummonId;          // unique for the match, never reused
  kind: SummonKind;
  owner: Hero;
  team: Team;
  slot: number;          // its place among the ones summoned with it: where it walks beside the owner, which flank it takes
  radius: number;        // SUMMONS[kind].radius, carried so bulkOf() needs no table lookup
  pos: THREE.Vector3;
  yaw: number;
  hp: number;
  maxHp: number;
  alive: boolean;
  until: number;         // sim time it expires
  life: number;          // total lifetime, for the view's fade
  target: Target | null;
  retargetAt: number;
  attackReadyAt: number;
  swingAt: number;       // last attack's start, for the view
  path: Point[];
  repathAt: number;
  marks: DamageMark[];   // Клеймо, same as a hero's or a monster's
}

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);

export function spawnSummon(g: Game, owner: Hero, kind: SummonKind, at: { x: number; z: number }, life: number, slot: number): Summon {
  const def = SUMMONS[kind];
  const p = def.fly || g.nav.isWalkable(at.x, at.z) ? at : g.nav.nearestWalkable(at.x, at.z);
  const s: Summon = {
    id: g.nextSummonId++, kind, owner, team: owner.team, slot, radius: def.radius,
    pos: new THREE.Vector3(p.x, 0, p.z), yaw: owner.yaw, hp: def.hp, maxHp: def.hp, alive: true,
    until: g.t + life, life, target: null, retargetAt: 0, attackReadyAt: g.t + 0.3, swingAt: -9,
    path: [], repathAt: 0, marks: [],
  };
  g.summons.push(s);
  g.emit({ type: 'fx', name: 'summon', pos: g.pos2(s.pos), kind, team: s.team }, s);
  return s;
}

/** The owner's living summons of one kind, in summoning order. */
export function summonsOf(g: Game, owner: Hero, kind: SummonKind): Summon[] {
  return g.summons.filter(s => s.alive && s.owner === owner && s.kind === kind);
}
export function golemOf(g: Game, owner: Hero): Summon | undefined {
  return g.summons.find(s => s.alive && s.owner === owner && s.kind === 'golem');
}

/* Seen like a totem: own team always, the enemy through fog and — in grass — only up close or through a reveal;
   a pet close by a hidden owner never (see the header). The one rule snapshots, replays, events and the offline
   view all read. */
export function summonVisibleTo(g: Game, team: Team, s: Summon): boolean {
  if (team === s.team) return true;
  const o = s.owner;
  if (s.kind !== 'golem' && o.alive && flatDist(s.pos, o.pos) <= PET_HIDE && !g.visibleTo(team, o)) return false;
  return g.totemVisibleTo(team, s);
}

export function updateSummons(g: Game, dt: number) {
  if (!g.monsterAi) return;  // a client's mirror: summons are puppets the snapshots place
  for (const s of g.summons) {
    if (!s.alive) continue;
    if (g.t >= s.until) { killSummon(g, s, null, false); continue; }
    if (s.marks.length) s.marks = s.marks.filter(k => k.until > g.t);
    act(g, s, dt);
  }
  if (g.summons.some(s => !s.alive)) g.summons = g.summons.filter(s => s.alive);
}

/* Where a target may still be chased from: the golem itself; the others, the living owner (itself once he's dead). */
const anchorOf = (s: Summon) => (s.kind !== 'golem' && s.owner.alive ? s.owner.pos : s.pos);

function canHit(g: Game, s: Summon, t: Target): boolean {
  if (!t.alive) return false;
  if (flatDist(t.pos, anchorOf(s)) - bulkOf(t) > SUMMONS[s.kind].leash) return false;
  if (isMonster(t)) return t.state !== 'return';
  if (t.team === s.team || !g.targetVisibleTo(s.team, t)) return false;
  return !isHero(t) || (!g.silhouette(t) && g.t >= t.cloakUntil);
}

function pickTarget(g: Game, s: Summon): Target | null {
  const def = SUMMONS[s.kind];
  if (s.kind !== 'golem') {
    const o = s.owner.alive && s.owner.order?.type === 'attack' ? s.owner.order.target : null;
    if (o && canHit(g, s, o)) return o;
  }
  let best: Target | null = null, bestD: number = def.aggro;
  const pool: Target[] = s.kind === 'golem' ? [...g.heroes, ...g.summons, ...g.monsters] : [...g.heroes, ...g.summons];
  for (const e of pool) {
    if (e === s || !canHit(g, s, e)) continue;
    const d = flatDist(e.pos, s.pos) - bulkOf(e);
    if (d < bestD) { best = e; bestD = d; }
  }
  return best;
}

function act(g: Game, s: Summon, dt: number) {
  const def = SUMMONS[s.kind];
  if (s.target && !canHit(g, s, s.target)) s.target = null;
  if (g.t >= s.retargetAt) { s.target = pickTarget(g, s); s.retargetAt = g.t + RETARGET; }
  const t = s.target;
  if (t) {
    const reach = def.range + bulkOf(t), d = flatDist(s.pos, t.pos);
    if (d <= reach) {
      s.path = [];
      s.yaw = Math.atan2(t.pos.x - s.pos.x, t.pos.z - s.pos.z);
      if (g.t >= s.attackReadyAt) strike(g, s, t);
      return;
    }
    // Wolves close in from the target's flanks (as seen from the owner), one each side — the pack surrounds it.
    let gx = t.pos.x, gz = t.pos.z;
    if (s.kind === 'wolf') {
      const from = anchorOf(s), a = Math.atan2(from.x - t.pos.x, from.z - t.pos.z) + (s.slot % 2 ? -1 : 1) * Math.PI / 2;
      const r = reach * 0.8, fx = t.pos.x + Math.sin(a) * r, fz = t.pos.z + Math.cos(a) * r;
      if (g.nav.isWalkable(fx, fz)) { gx = fx; gz = fz; }
    }
    moveToward(g, s, gx, gz, dt, 0);
    return;
  }
  if (s.kind === 'golem') { const p = pointFor(g, s); moveToward(g, s, p.x, p.z, dt, 0.8); return; }
  if (!s.owner.alive) { s.path = []; return; }
  // Beside the owner, each on its own side.
  const o = s.owner.pos, a = s.owner.yaw + Math.PI + (s.slot - 1) * 1.1;
  moveToward(g, s, o.x + Math.sin(a) * 1.6, o.z + Math.cos(a) * 1.6, dt, 0.8);
}

/* The golem's post: the nearest capture point its team doesn't hold, or the nearest one when it holds them all. */
function pointFor(g: Game, s: Summon): { x: number; z: number } {
  const byDist = [...g.points].sort((x, y) => flatDist(x.pos, s.pos) - flatDist(y.pos, s.pos));
  const p = byDist.find(q => q.owner !== s.team) ?? byDist[0];
  return p ? p.pos : { x: LAYOUT.points[0]!.pos[0], z: LAYOUT.points[0]!.pos[1] };
}

function moveToward(g: Game, s: Summon, x: number, z: number, dt: number, stopWithin: number) {
  if (Math.hypot(x - s.pos.x, z - s.pos.z) <= stopWithin) { s.path = []; return; }
  const def = SUMMONS[s.kind];
  if (def.fly) {
    const dx = x - s.pos.x, dz = z - s.pos.z, d = Math.hypot(dx, dz), step = Math.min(d, def.speed * dt);
    if (d > 1e-4) { s.pos.x += dx / d * step; s.pos.z += dz / d * step; s.yaw = Math.atan2(dx, dz); }
    return;
  }
  if (g.t >= s.repathAt || !s.path.length) { s.path = g.nav.findPath(s.pos.x, s.pos.z, x, z); s.repathAt = g.t + REPATH; }
  let step = def.speed * g.groundSpeed(s.pos.x, s.pos.z) * dt;
  while (step > 0 && s.path.length) {
    const wp = s.path[0] as Point, dx = wp.x - s.pos.x, dz = wp.z - s.pos.z, d = Math.hypot(dx, dz);
    if (d > 1e-4) s.yaw = Math.atan2(dx, dz);
    if (d <= step) { s.pos.set(wp.x, 0, wp.z); s.path.shift(); step -= d; }
    else { s.pos.x += dx / d * step; s.pos.z += dz / d * step; step = 0; }
  }
}

function strike(g: Game, s: Summon, t: Target) {
  s.attackReadyAt = g.t + SUMMONS[s.kind].attackCd;
  s.swingAt = g.t;
  const dealt = g.damage(t, SUMMONS[s.kind].dmg, s.owner, { skipSrcBuff: true, fromPos: s.pos, by: s });
  if (s.kind === 'magpie' && dealt > 0 && isHero(t) && t.alive && PECK_DELAYS.has(t.state)) t.stateT -= SKILLS.birdcatcher.Q.peckDelay;
}

/* Entered from Game.damage() after the attacker's own buffs and Клеймо: only hp is lost. */
export function damageSummon(g: Game, s: Summon, amount: number, src: Hero | null, skill: boolean, by: Target | null): number {
  if (!g.monsterAi || !s.alive || amount <= 0) return 0;
  const dealt = Math.min(amount, s.hp);
  s.hp -= amount;
  // The summon as subject: seen only by whoever sees it (grass included), even once it has died this tick.
  if (skill) g.emit({ type: 'summonNum', summon: s.id, value: Math.round(amount) }, s);
  g.emitHit({ type: 'fx', name: 'hit', pos: g.pos2(s.pos), color: src && src.def.bolt ? src.def.bolt : (src && src.team === 'a' ? 0xffb347 : 0x7fe0ff), big: skill }, s, by);
  if (s.hp <= 0) killSummon(g, s, src, true);
  return dealt;
}

/* `destroyed`: its hp ran out (not its time) — only then does Мастер shield a living Кукольник. Stays in
   g.summons (alive: false) until the end of this tick's updateSummons, so references held this tick see it dead. */
export function killSummon(g: Game, s: Summon, _killer: Hero | null, destroyed: boolean) {
  s.alive = false;
  s.hp = Math.max(0, s.hp);
  s.target = null;
  s.path = [];
  s.marks = [];
  g.emit({ type: 'fx', name: 'summonGone', pos: g.pos2(s.pos), kind: s.kind, destroyed }, s);
  const o = s.owner;
  if (destroyed && s.kind === 'golem' && o.kind === 'puppeteer' && o.alive) {
    const P = PASSIVES.puppeteer, amount = maxHpOf(o) * P.shield;
    // src null: an expiring Мастер shield must not refund anyone's cooldown the way an Оберег does.
    if (!o.shield || o.shield.until <= g.t || o.shield.amount < amount) o.shield = { amount, until: g.t + P.life, src: null };
    g.stats.passive(o.id, amount);
    g.emit({ type: 'fx', name: 'ward', hero: o.id });
  }
}

/* Зоркий глаз: every magpie sees this far around itself, over trees (it flies) — the same fog rule as a hero's
   sight otherwise, grass included. */
export function birdSight(s: Summon): number { return s.kind === 'magpie' ? PASSIVES.birdcatcher.sight : 0; }

/* Зоркий глаз's stat: seconds an enemy hero stood inside a living magpie's sight, counted once per enemy per
   tick however many birds saw it. */
export function countBirdSight(g: Game, dt: number) {
  for (const h of g.heroes) {
    if (!h.alive) continue;
    const bird = g.summons.find(s => s.alive && s.kind === 'magpie' && s.team !== h.team && flatDist(s.pos, h.pos) < birdSight(s));
    if (bird) g.stats.passive(bird.owner.id, dt);
  }
}
